import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import {
  createPaymentAutomation,
  deletePaymentAutomation,
  listPaymentAutomations,
  updatePaymentAutomation,
} from '../services/paymentService';
import { PaymentAutomationActionType, PaymentAutomationRule } from '../types/Payment';
import styles from '../Payments.module.css';

const emptyForm = {
  name: '',
  isEnabled: true,
  actionType: 'conversation_message' as PaymentAutomationActionType,
  templateText: '',
  taskTitle: '',
  taskDescription: '',
};

export const PaymentAutomationsPage: React.FC = () => {
  const { t } = useTranslation(['payments', 'common']);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PermissionScopes.paymentsSettings);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rules, setRules] = React.useState<PaymentAutomationRule[]>([]);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState(emptyForm);
  const [formOpen, setFormOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRules(await listPaymentAutomations());
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToLoadAutomations'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (rule: PaymentAutomationRule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name,
      isEnabled: rule.isEnabled,
      actionType: rule.actionType,
      templateText: rule.templateText ?? '',
      taskTitle: rule.taskTitle ?? '',
      taskDescription: rule.taskDescription ?? '',
    });
    setFormOpen(true);
  };

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      isEnabled: form.isEnabled,
      trigger: 'paid' as const,
      actionType: form.actionType,
      templateText: form.actionType === 'conversation_message' ? form.templateText.trim() : undefined,
      taskTitle: form.actionType === 'create_task' ? form.taskTitle.trim() : undefined,
      taskDescription: form.actionType === 'create_task' ? form.taskDescription.trim() : undefined,
    };
    try {
      if (editingId) {
        await updatePaymentAutomation(editingId, payload);
      } else {
        await createPaymentAutomation(payload);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToSaveRule'));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (rule: PaymentAutomationRule) => {
    if (!window.confirm(t('confirmDeleteRule'))) return;
    setSaving(true);
    setError(null);
    try {
      await deletePaymentAutomation(rule.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToSaveRule'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner text={t('loading')} />;
  }

  return (
    <div className={styles.stack}>
      <div className={styles.sectionCard}>
        <div className={styles.headerRow}>
          <div>
            <h3 className={styles.sectionTitle}>{t('automationsTitle')}</h3>
            <p className={styles.sectionIntro}>{t('automationsIntro')}</p>
          </div>
          {canEdit ? (
            <button className="btn btn-primary" type="button" onClick={openCreate}>{t('addRule')}</button>
          ) : null}
        </div>
        {error ? <div className={styles.error}>{error}</div> : null}
        {rules.length === 0 ? <p className={styles.emptyState}>{t('noRules')}</p> : null}
        {rules.map(rule => (
          <div key={rule.id} className={styles.ruleCard}>
            <div className={styles.ruleHeader}>
              <div>
                <strong>{rule.name}</strong>
                <div className={styles.muted}>
                  {t('triggerPaid')} · {rule.actionType === 'create_task' ? t('actionCreateTask') : t('actionConversationMessage')}
                  {rule.isEnabled ? '' : ` · ${t('ruleEnabled')}: off`}
                </div>
              </div>
              {canEdit ? (
                <div className={styles.actionsCell}>
                  <button className="btn btn-secondary" type="button" disabled={saving} onClick={() => openEdit(rule)}>{t('editRule')}</button>
                  <button className="btn btn-secondary" type="button" disabled={saving} onClick={() => void onDelete(rule)}>{t('deleteRule')}</button>
                </div>
              ) : null}
            </div>
            {rule.templateText ? <p className={styles.hint}>{rule.templateText}</p> : null}
            {rule.taskTitle ? <p className={styles.hint}>{rule.taskTitle}</p> : null}
          </div>
        ))}
      </div>

      {formOpen ? (
        <form className={styles.sectionCard} onSubmit={onSave}>
          <h3 className={styles.sectionTitle}>{editingId ? t('editRule') : t('addRule')}</h3>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.label}>{t('ruleName')}</label>
              <input value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} required />
            </div>
            <label className={`${styles.field} ${styles.checkboxRow}`}>
              <input
                type="checkbox"
                checked={form.isEnabled}
                onChange={event => setForm(current => ({ ...current, isEnabled: event.target.checked }))}
              />
              {t('ruleEnabled')}
            </label>
            <div className={styles.field}>
              <label className={styles.label}>{t('actionType')}</label>
              <select
                value={form.actionType}
                onChange={event => setForm(current => ({ ...current, actionType: event.target.value as PaymentAutomationActionType }))}
              >
                <option value="conversation_message">{t('actionConversationMessage')}</option>
                <option value="create_task">{t('actionCreateTask')}</option>
              </select>
            </div>
            {form.actionType === 'conversation_message' ? (
              <div className={`${styles.field} ${styles.fieldWide}`}>
                <label className={styles.label}>{t('templateText')}</label>
                <textarea
                  value={form.templateText}
                  onChange={event => setForm(current => ({ ...current, templateText: event.target.value }))}
                />
                <p className={styles.hint}>{t('templateHint')}</p>
              </div>
            ) : (
              <>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <label className={styles.label}>{t('taskTitle')}</label>
                  <input value={form.taskTitle} onChange={event => setForm(current => ({ ...current, taskTitle: event.target.value }))} />
                </div>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <label className={styles.label}>{t('taskDescription')}</label>
                  <textarea
                    value={form.taskDescription}
                    onChange={event => setForm(current => ({ ...current, taskDescription: event.target.value }))}
                  />
                </div>
              </>
            )}
          </div>
          <div className={styles.formActions}>
            <button className="btn btn-secondary" type="button" disabled={saving} onClick={() => setFormOpen(false)}>{t('close')}</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{t('save')}</button>
          </div>
        </form>
      ) : null}
    </div>
  );
};
